import React, { useState } from 'react'
import BrandLogo from '@/assets/svg/White-logo.svg'
import { PrimaryButton } from '@/shared/components/ui/Button/PrimaryButton'
import CommonTextArea from '@/shared/components/ui/Input/CommonTextArea'
import SquircleIcon from '@/shared/components/ui/SquircleIcon'
import { ACCOUNT_DISCOVER_SUGGESTIONS } from '@/shared/utils/accountConfig'
import PersonalizedSuggestions from '@/features/contacts/components/PersonalizedSuggestions'
import {
    ArrowRight,
    Bookmark,
    ChevronDown,
    ChevronUp,
    MoreHorizontal,
    Pencil,
    Trash2,
} from 'lucide-react'
import CommonDropdownMenu from '@/shared/components/ui/DropDown/DropdownMenu'
import {
    useCreateCustomAccountQuery,
    useFetchCustomAccountQueries,
    useRunCustomAccountQuery,
    useSelectCustomAccountQuery,
} from '@/features/accounts/hooks/useAccounts'
import { useMainStore } from '@/stores'
import { safeFormatTimeAgo } from '@/features/dashboard/utils/accountsDashboardUtils'

export interface CustomQueryItem {
    id: string | number;
    name: string;
    query: string;
    created_at?: string;
    updated_at?: string;
    last_run_at?: string;
    last_run_status?: string;
    recommendations?: any[];
    new_count?: number;
    latest_run?: {
        candidate_count?: number;
        status?: string;
    };
    [key: string]: any;
}

interface CustomSearchProps {
    value?: string;
    onChange?: (value: string) => void;
    onSearch?: (query: string) => void;
    isLoading?: boolean;
    customQueries?: CustomQueryItem[];
    selectedCustomQueryId?: string | number;
    onSelectQuery?: (query: CustomQueryItem) => void;
    onRunQuery?: (query: CustomQueryItem) => void;
    onRenameQuery?: (query: CustomQueryItem) => void;
    onDeleteQuery?: (query: CustomQueryItem) => void;
}

const CustomSearch: React.FC<CustomSearchProps> = ({
    value: controlledValue,
    onChange: controlledOnChange,
    onSearch,
    isLoading: controlledIsLoading,
    customQueries: propCustomQueries,
    selectedCustomQueryId,
    onSelectQuery,
    onRunQuery,
    onRenameQuery,
    onDeleteQuery,
}) => {
    const user = useMainStore((state) => state.user);
    const [internalValue, setInternalValue] = useState('');
    const [showAllSaved, setShowAllSaved] = useState(false);
    const [showExampleSearches, setShowExampleSearches] = useState(false);

    const isControlled = controlledValue !== undefined;
    const query = isControlled ? controlledValue : internalValue;

    const setQuery = (nextValue: string) => {
        if (!isControlled) {
            setInternalValue(nextValue);
        }
        controlledOnChange?.(nextValue);
    };

    // Fallback hooks if props not provided
    const { data: fetchedCustomQueries = [] } = useFetchCustomAccountQueries({
        userId: user?.id,
        enabled: Boolean(user?.id) && !propCustomQueries,
    });
    const { mutate: createCustomQuery, isPending: isMutationPending, } = useCreateCustomAccountQuery();
    const { mutate: runCustomQuery } = useRunCustomAccountQuery();
    const { mutate: selectCustomQuery } = useSelectCustomAccountQuery();

    const displayQueries: CustomQueryItem[] = propCustomQueries ?? fetchedCustomQueries;
    const isLoading = controlledIsLoading !== undefined ? controlledIsLoading : isMutationPending;

    const handleSearch = () => {
        const trimmed = query.trim();
        if (!trimmed || isLoading) return;

        if (onSearch) {
            onSearch(trimmed);
        } else {
            createCustomQuery({
                name: trimmed.slice(0, 80),
                query: trimmed,
            });
        }
    };

    const handleSuggestionClick = (label: string) => {
        setQuery(label);
    };

    const handleSelectQueryItem = (item: CustomQueryItem) => {
        setQuery(item.query || '');
        if (onSelectQuery) {
            onSelectQuery(item);
        } else if (item.id) {
            selectCustomQuery({ queryId: String(item.id) });
        }
    };

    const handleRename = (item: CustomQueryItem) => {
        onRenameQuery?.(item);
    };

    const handleDelete = (item: CustomQueryItem) => {
        onDeleteQuery?.(item);
    };

    const getResultCount = (item: CustomQueryItem) => {
        if (Array.isArray(item.recommendations) && item.recommendations.length > 0) {
            return item.recommendations.length;
        }
        if (item.latest_run?.candidate_count) {
            return item.latest_run.candidate_count;
        }
        return 0;
    };

    const getNewCount = (item: CustomQueryItem) => {
        if (typeof item.new_count === 'number' && item.new_count > 0) {
            return item.new_count;
        }
        if (Array.isArray(item.recommendations)) {
            const available = item.recommendations.filter(
                (r) => r.recommendation_status === 'available' || r.is_new,
            );
            if (available.length > 0 && available.length < item.recommendations.length) {
                return available.length;
            }
        }
        return 0;
    };

    const hasSavedQueries = displayQueries && displayQueries.length > 0;
    const visibleQueries = showAllSaved ? displayQueries : displayQueries.slice(0, 3);

    return (
        <div className='flex flex-col gap-8 h-full w-full items-center justify-center py-12'>
            <div className='flex flex-col gap-8 w-[740px]'>
                {/* title with branding */}
                <div className='flex flex-col gap-3'>
                    <SquircleIcon size={'w-11 h-11'} className='flex self-center'>
                        <img src={BrandLogo} alt='brand-logo' height={31} width={19} />
                    </SquircleIcon>
                    <h3 className='text-2xl font-medium text-black dark:text-white font-sans text-center'>
                        Which companies are you looking for?
                    </h3>
                </div>

                {/* text area with button */}
                <div className='relative w-full'>
                    <CommonTextArea
                        value={query}
                        placeholder='Describe the companies you want to find...'
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setQuery(e.target.value)}
                        onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSearch();
                            }
                        }}
                        rows={4}
                        className='w-full px-4 py-4 pb-16 bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-2xl'
                        textAreaClassName='px-0 py-0 placeholder:text-gray-400 placeholder:text-sm'
                    />
                    <div className='absolute bottom-4 right-4'>
                        <PrimaryButton
                            startContent={false}
                            endContent={<ArrowRight size={16} className='text-white' />}
                            onClick={handleSearch}
                            isLoading={isLoading}
                            disabled={!query.trim() || isLoading}
                        >
                            {isLoading ? 'Finding Accounts...' : 'Find Accounts'}
                        </PrimaryButton>
                    </div>
                </div>

                {/* Saved searches or suggestions */}
                {hasSavedQueries ? (
                    <div className='flex flex-col gap-4'>
                        {/* Header with count and Show fewer / Show all */}
                        <div className='flex items-center justify-between w-full'>

                            <h5 className='text-sm font-medium text-gray-500'>
                                Your Saved Searches <span className='text-gray-500'>· {displayQueries.length}</span>
                            </h5>
                            {displayQueries.length > 5 && (
                                <button
                                    type='button'
                                    onClick={() => setShowAllSaved(!showAllSaved)}
                                    className='flex items-center gap-1 text-sm font-medium text-secondary dark:text-gray-400 hover:text-secondary dark:hover:text-gray-200 transition-colors cursor-pointer'
                                >
                                    <span>{showAllSaved ? 'Show fewer' : 'Show all'}</span>
                                    {showAllSaved ? <ChevronUp size={16} strokeWidth={2} /> : <ChevronDown size={16} strokeWidth={2} />}
                                </button>
                            )}
                        </div>

                        {/* Saved searches list card */}
                        <div className='rounded-2xl border border-gray-200 h-[calc(100dvh-660px)] overflow-y-auto dark:border-gray-800 bg-white dark:bg-[#0f141c] overflow-hidden divide-y divide-gray-100 dark:divide-gray-800/80 shadow-sm'>
                            {visibleQueries.map((item) => {
                                const resultCount = getResultCount(item);
                                const newCount = getNewCount(item);
                                const isSelected = String(selectedCustomQueryId) === String(item.id);

                                return (
                                    <div
                                        key={item.id}
                                        onClick={() => handleSelectQueryItem(item)}
                                        className={`flex items-center justify-between px-4 py-3.5 hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors cursor-pointer group relative ${isSelected ? 'bg-secondary/5 dark:bg-secondary/10' : ''
                                            }`}
                                    >
                                        {/* Left: Icon & Query Info */}
                                        <div className='flex items-center gap-3 min-w-0 pr-4'>
                                            <Bookmark
                                                size={16}
                                                className={`shrink-0 transition-colors ${isSelected
                                                    ? 'text-secondary fill-secondary'
                                                    : 'text-gray-400 dark:text-gray-500 group-hover:text-secondary'
                                                    }`}
                                            />
                                            <div className='flex flex-col min-w-0'>
                                                <span className='text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-secondary transition-colors truncate'>
                                                    {item.name || item.query}
                                                </span>

                                            </div>
                                        </div>

                                        {/* Right: Badge, Results count, Time & Actions */}
                                        <div className='flex items-center gap-3 shrink-0 relative'>
                                            {newCount > 0 && (
                                                <span className='px-2 py-0.5 text-[11px] font-medium rounded-full bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-gray-200'>
                                                    {newCount} new
                                                </span>
                                            )}
                                            <span className='text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap'>
                                                {resultCount} results · {safeFormatTimeAgo(item.last_run_at || item.created_at)}
                                            </span>

                                            {/* Action dropdown via CommonDropdownMenu */}
                                            <div onClick={(e) => e.stopPropagation()}>
                                                <CommonDropdownMenu
                                                    actions={[
                                                        {
                                                            id: 'rename',
                                                            name: 'Rename',
                                                            icon: <Pencil size={14} />,
                                                            onClick: () => handleRename(item),
                                                        },
                                                        {
                                                            id: 'delete',
                                                            name: 'Delete',
                                                            isDanger: true,
                                                            icon: <Trash2 size={14} className='text-red-500' />,
                                                            onClick: () => handleDelete(item),
                                                        },
                                                    ]}
                                                    itemId={item.id}
                                                    triggerBtn={
                                                        <button
                                                            type='button'
                                                            className='p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer'
                                                        >
                                                            <MoreHorizontal size={16} />
                                                        </button>
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    /* No saved searches yet: suggestions written from the user's company */
                    <PersonalizedSuggestions
                        kind='accounts'
                        fallback={ACCOUNT_DISCOVER_SUGGESTIONS}
                        onPick={handleSuggestionClick}
                    />
                )}
            </div>
        </div>
    );
};

export default CustomSearch;